import React, { useMemo } from 'react';
import SegmentedPolylineOverlay from './SegmentedPolylineOverlay';

const fallbackRanges = [
  { min: 0, max: 15, color: '#d32f2f' },   // red
  { min: 15, max: 38, color: '#f57c00' },  // orange
  { min: 38, max: 43, color: '#fbc02d' },  // yellow
  { min: 43, max: 60, color: '#388e3c' },  // green
  { min: 60, max: Infinity, color: '#1976d2' }, // blue
];

function buildPressureRanges(targetPressurePsi) {
  if (!targetPressurePsi || targetPressurePsi <= 0) return fallbackRanges;

  const t = targetPressurePsi;
  return [
    { min: 0,          max: t * 0.5, color: '#d32f2f' },   // red
    { min: t * 0.5,    max: t * 0.7, color: '#f57c00' },   // orange
    { min: t * 0.7,    max: t * 0.9, color: '#fbc02d' },   // yellow
    { min: t * 0.9,    max: t * 1.1, color: '#388e3c' },   // green
    { min: t * 1.1,    max: t * 1.3, color: '#1976d2' },   // blue
    { min: t * 1.3,    max: t * 1.5, color: '#3f51b5' },   // indigo
    { min: t * 1.5,    max: Infinity, color: '#7b1fa2' },   // purple
  ];
}

function getRangesWithOff(allPoints, pressureRanges) {
  const hasOff = allPoints.some(pt => pt.weedItOff);
  if (hasOff) {
    return [
      {
        min: 'off',
        max: 'off',
        color: '#d32f2f',
        value: 'Weed-It Off',
        dashed: true,
      },
      ...pressureRanges
    ];
  }
  return pressureRanges;
}

const legendFormatter = r =>
  r.value === 'Weed-It Off'
    ? r.value
    : r.max === Infinity
      ? `${Math.round(r.min)}+`
      : `${Math.round(r.min)} - ${Math.round(r.max)}`;

export default function WeedItPressureOverlay({ routes, tractor }) {
  const allPoints = useMemo(() => routes.flat(), [routes]);
  const pressureRanges = useMemo(
    () => buildPressureRanges(tractor?.targetPressurePsi),
    [tractor?.targetPressurePsi]
  );
  const finalRanges = useMemo(
    () => getRangesWithOff(allPoints, pressureRanges),
    [allPoints, pressureRanges]
  );

  const valueSelector = pt =>
    pt.weedItOff ? 'off' : (pt.weedItPressurePsi >= 0 ? pt.weedItPressurePsi : 0);

  const targetPsi = tractor?.targetPressurePsi;
  const legendTitle = targetPsi && targetPsi > 0
    ? <>Pressure (psi)<br /><span style={{ fontSize: '0.9em', fontWeight: 'normal' }}>Target: {Math.round(targetPsi)} psi</span></>
    : "Pressure (psi)";

  if (!allPoints.length) return null;

  return (
    <SegmentedPolylineOverlay
      segments={routes}
      valueSelector={valueSelector}
      ranges={finalRanges}
      legendTitle={legendTitle}
      legendFormatter={legendFormatter}
      units="psi"
      discrete={false}
    />
  );
}